/* @jsx h */
import { defineComponent, h, onMounted, ref, type Ref } from "vue";
import {
  getLocales,
  loadCountries,
  loadCurrencies,
  loadTimezones,
} from "../../ManagerSettings/subpages/ProfileSettings/helpers/countryData";
import type {
  CountryOption,
  CurrencyOption,
  TimezoneOption,
} from "../../ManagerSettings/subpages/ProfileSettings/types";

type RegionalField = "jurisdiction" | "timezone" | "currency" | "locale";

export const RestaurantRegionalSettings = defineComponent<{
  jurisdiction: string;
  timezone: string;
  currency: string;
  locale: string;
  onUpdate: (field: RegionalField, value: string) => void;
}>({
  name: "RestaurantRegionalSettings",
  props: ["jurisdiction", "timezone", "currency", "locale", "onUpdate"],
  setup(props) {
    const countries: Ref<CountryOption[]> = ref([]);
    const currencies: Ref<CurrencyOption[]> = ref([]);
    const timezones: Ref<TimezoneOption[]> = ref([]);
    const loading = ref(true);
    const locales = getLocales();

    onMounted(async () => {
      [countries.value, timezones.value, currencies.value] = await Promise.all([
        loadCountries(),
        loadTimezones(),
        loadCurrencies(),
      ]);
      loading.value = false;
    });

    const fieldClass =
      "select select-bordered w-full bg-base-200 transition-colors focus:bg-base-100";

    const updateCountry = (countryCode: string) => {
      props.onUpdate("jurisdiction", countryCode);
      const country = countries.value.find(
        (option) => option.value === countryCode,
      );
      if (!country) return;

      if (country.timezone) props.onUpdate("timezone", country.timezone);
      if (country.currency) props.onUpdate("currency", country.currency);
      if (
        country.locale &&
        locales.some((locale) => locale.value === country.locale)
      ) {
        props.onUpdate("locale", country.locale);
      }
    };

    return () => (
      <div class="grid gap-4 md:grid-cols-2">
        <label class="form-control">
          <span class="label-text mb-2 font-medium text-base-content/80">
            Country / jurisdiction *
          </span>
          <select
            class={fieldClass}
            value={props.jurisdiction}
            disabled={loading.value}
            required
            onChange={(event) =>
              updateCountry((event.target as HTMLSelectElement).value)
            }
          >
            <option value="">
              {loading.value ? "Loading countries..." : "Select country"}
            </option>
            {countries.value.map((country) => (
              <option key={country.value} value={country.value}>
                {country.flag ? `${country.flag} ` : ""}
                {country.label}
              </option>
            ))}
          </select>
        </label>

        <label class="form-control">
          <span class="label-text mb-2 font-medium text-base-content/80">
            Timezone *
          </span>
          <select
            class={fieldClass}
            value={props.timezone}
            disabled={loading.value}
            required
            onChange={(event) =>
              props.onUpdate(
                "timezone",
                (event.target as HTMLSelectElement).value,
              )
            }
          >
            <option value="">
              {loading.value ? "Loading timezones..." : "Select timezone"}
            </option>
            {timezones.value.map((timezone) => (
              <option key={timezone.value} value={timezone.value}>
                {timezone.label} ({timezone.offset})
              </option>
            ))}
          </select>
        </label>

        <label class="form-control">
          <span class="label-text mb-2 font-medium text-base-content/80">
            Currency *
          </span>
          <select
            class={fieldClass}
            value={props.currency}
            disabled={loading.value}
            required
            onChange={(event) =>
              props.onUpdate(
                "currency",
                (event.target as HTMLSelectElement).value,
              )
            }
          >
            <option value="">
              {loading.value ? "Loading currencies..." : "Select currency"}
            </option>
            {currencies.value.map((currency) => (
              <option key={currency.value} value={currency.value}>
                {currency.symbol} - {currency.label}
              </option>
            ))}
          </select>
        </label>

        <label class="form-control">
          <span class="label-text mb-2 font-medium text-base-content/80">
            Language / locale *
          </span>
          <select
            class={fieldClass}
            value={props.locale}
            required
            onChange={(event) =>
              props.onUpdate(
                "locale",
                (event.target as HTMLSelectElement).value,
              )
            }
          >
            <option value="">Select locale</option>
            {locales.map((locale) => (
              <option key={locale.value} value={locale.value}>
                {locale.label}
              </option>
            ))}
          </select>
        </label>
      </div>
    );
  },
});
